(ns hai.home.mobile.mini-apps
  "Shared card frame for the mobile dashboard's mini-apps: a tinted header with
   an icon/title link and round action buttons, plus a placeholder body. Mirrors
   [[hai.mini-apps.frame]] but tuned for the narrow layout — no duck-tape
   strip, softer elevation, and larger touch targets. Each mini-app (notes,
   tasks, calendar, progress) inherits this card in its own ns."
  (:require
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

(def ^:private chevron-icon (icons/mui-icon :outlined :chevron_right))

(o/defstyled card :section
  {:background t/--card-surface
   :border-radius "20px"
   :box-shadow "0 1px 2px rgba(45,62,42,0.04), 0 4px 14px rgba(45,62,42,0.05)"
   :display "flex" :flex-direction "column"
   :position "relative"
   :color t/--card-forest}
  ;; Same minimum as the desktop frame, so a card without action buttons keeps
  ;; the header height of one that has them.
  [:.app-header
   {:display "flex" :align-items "center" :gap "10px"
    :padding "12px 16px"
    :min-height "56px"
    :border-top-left-radius "20px" :border-top-right-radius "20px"}]
  [:.app-icon
   {:display "inline-flex" :align-items "center"
    :color t/--note-kraft}
   ;; The page applies `svg {fill: currentColor}`; stroke icons need fill: none
   ;; restored or their shapes fill solid.
   [:svg {:fill "none"}]]
  [:.app-title
   {:font-size "16px" :font-weight "700" :line-height "1.2"
    :margin "0"
    :letter-spacing "-0.005em"
    :color t/--note-kraft}]
  ;; Icon + title as one link to the app's full page.
  [:.app-title-link
   {:display "inline-flex" :align-items "center" :gap "10px"
    :color "inherit"
    :text-decoration "none"
    :cursor "pointer"}
   [:&:hover
    [:.app-title {:text-decoration "underline" :text-underline-offset "3px"}]]]
  [:.app-actions
   {:margin-left "auto"
    :display "flex" :align-items "center" :gap "6px"}]
  [:.app-action-btn
   {:width "32px" :height "32px"
    :border "none" :border-radius "999px"
    :background "transparent"
    :padding "0"
    :color t/--note-kraft
    :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :transition "background 0.15s ease"}
   [:svg {:fill "currentColor"}]]
  ;; Hover feedback only where hover exists — a tap's sticky :hover would leave
  ;; the button tinted until the next tap elsewhere.
  [:at-media {:hover "hover"}
   [:.app-action-btn [:&:hover {:background t/--note-kraft-10}]]]
  [:.app-placeholder
   {:padding "28px 16px"
    :font-size "13px"
    :color t/--link-muted
    :text-align "center"}])

;; ---------------------------------------------------------------------------
;; Collapsible section — groups a card's rows under a chevron/label/count toggle
;; ---------------------------------------------------------------------------

(o/defstyled section :div
  {:border-top "1px solid" :border-top-color t/--card-forest-10}
  [:&:first-child {:border-top "0"}]
  [:&:last-child
   {:overflow "hidden"
    :border-bottom-left-radius "20px"
    :border-bottom-right-radius "20px"}])

(o/defstyled section-toggle :button
  {:display "flex" :align-items "center" :gap "10px"
   :width "100%"
   :padding "12px 16px"
   :border "none" :background "transparent"
   :font-family "inherit"
   :text-align "left"
   :cursor "pointer"
   :transition "background 0.15s ease"}
  [:at-media {:hover "hover"}
   [:&:hover {:background t/--note-kraft-10}]]
  [:.section-chevron
   {:display "inline-flex" :align-items "center"
    :color t/--note-kraft}
   [:svg {:width "16px" :height "16px" :fill "currentColor"
          :transition "transform 0.15s ease"}]]
  [:&.open [:.section-chevron [:svg {:transform "rotate(90deg)"}]]]
  [:.section-label
   {:font-size "13px" :font-weight "700"
    :letter-spacing "0.04em" :text-transform "uppercase"
    :color t/--note-kraft}]
  [:.section-count
   {:font-size "13px" :font-weight "500"
    :color t/--link-muted}])

(o/defstyled section-empty :div
  {:padding "0 16px 14px"
   :font-size "13px"
   :color t/--link-muted})

(defn section-view
  "A collapsible section: a chevron + uppercase LABEL + count toggle over
   `body`, shown only when `open?`. `on-toggle` flips it; an absent/nil `body`
   shows `empty-label` (default \"Nothing here.\")."
  [{:keys [label item-count open? on-toggle empty-label]} body]
  [section
   [section-toggle
    {:type "button"
     :class (when open? "open")
     :on-click on-toggle}
    [:span.section-chevron [chevron-icon]]
    [:span.section-label label]
    [:span.section-count item-count]]
   (when open?
     (or body [section-empty (or empty-label "Nothing here.")]))])
